import { motion } from "framer-motion";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const EMAIL = "marco.solissanchez@gmail.com";
const PHONE = "+52 1 442 607 65 55";

const Marquee = ({ words }) => (
  <div className="overflow-hidden border-y border-white/10 bg-[#0a0a0a]">
    <div className="flex marquee-track whitespace-nowrap py-6 font-display uppercase text-5xl md:text-7xl tracking-tight text-white/90">
      {Array.from({ length: 2 }).map((_, k) => (
        <div key={k} className="flex items-center gap-10 px-10 shrink-0">
          {words.map((w, idx) => (
            <span key={`${k}-${idx}`} className="flex items-center gap-10">
              <span>{w}</span>
              <span className="text-[#ff3b00]">●</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const Footer = () => {
  const { t } = useT();
  const f = t.footer;
  return (
    <footer
      id="contact"
      data-testid={FOOTER.section}
      className="relative bg-[#0a0a0a]"
    >
      <Marquee words={f.marquee} />

      <div className="px-6 md:px-12 lg:px-24 pt-20 md:pt-28 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-7">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff3b00] mb-4">
              {f.eyebrow}
            </div>
            <motion.h2
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-[88px] leading-[0.9] tracking-tight"
            >
              {f.claimA}<br />
              <span className="text-white/40">{f.claimB}</span>
              <span className="text-[#ff3b00]">{f.claimC}</span>
            </motion.h2>

            <a
              href={`mailto:${EMAIL}`}
              data-testid={FOOTER.email}
              className="group mt-10 inline-flex items-center gap-4 font-display uppercase text-2xl md:text-4xl lg:text-5xl tracking-tight text-white hover:text-[#ff3b00] transition-colors"
            >
              <Mail className="w-6 h-6 md:w-8 md:h-8 shrink-0 text-[#ff3b00]" strokeWidth={1.5} />
              <span className="underline decoration-1 underline-offset-[6px] decoration-white/30 group-hover:decoration-[#ff3b00]">
                {f.emailLabel}
              </span>
              <ArrowUpRight className="w-5 h-5 md:w-7 md:h-7 shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
            </a>
          </div>

          <div className="md:col-span-4 md:col-start-9 flex flex-col gap-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">
                {f.lineLabel}
              </div>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                data-testid={FOOTER.phone}
                className="inline-flex items-center gap-3 font-display uppercase text-2xl md:text-3xl tracking-tight text-white hover:text-[#ff3b00] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#ff3b00]" strokeWidth={1.5} />
                {PHONE}
              </a>
            </div>

            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-4">
                {f.availLabel}
              </div>
              <div className="flex flex-col gap-3 font-mono text-sm tracking-[0.15em] uppercase text-white/70">
                <div className="flex items-center justify-between py-3 border-t border-white/10">
                  <span>{f.briefs}</span>
                  <span className="text-[#ff3b00]">{f.briefsValue}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-t border-b border-white/10">
                  <span>{f.response}</span>
                  <span className="text-white">{f.responseValue}</span>
                </div>
              </div>
            </div>

            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 leading-relaxed">
              {f.hq}<br />
              {f.serving}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">
          <span>{f.copyright}</span>
          <span className="hidden md:inline">{f.tag}</span>
          <span className="flex items-center gap-2">
            {f.statusLabel}<span className="text-[#ff3b00]">{f.statusValue}</span>
            <span className="w-1.5 h-1.5 bg-[#ff3b00] blink inline-block" />
          </span>
        </div>
      </div>
    </footer>
  );
};
